/**
 * ADR-0156 §10 — pure display logic for the Admin → Backup page. Extracted to
 * lib/ per console convention (tests are `*.test.ts`; no render harness).
 */

import { formatTimestamp } from "./formatTime.js";

export type BackupEndpointForm = "ssh" | "local" | "unknown";

/** Endpoint-form detection drives the "Same-host placement" warning pill (§6.4). */
export function endpointForm(endpoint: string): BackupEndpointForm {
  const e = endpoint.trim();
  if (e.startsWith("ssh://")) return "ssh";
  if (e.startsWith("/")) return "local";
  return "unknown";
}

/** Client-side §3.3 grammar check for validate-on-blur (the hub re-validates). */
export function endpointLooksValid(endpoint: string): boolean {
  const e = endpoint.trim();
  if (e.startsWith("ssh://")) {
    return /^ssh:\/\/[A-Za-z0-9._-]+@[A-Za-z0-9._-]+(:\d{1,5})?\/[A-Za-z0-9/._-]+$/.test(e);
  }
  return /^\/[A-Za-z0-9/._-]+$/.test(e);
}

/**
 * Absolute timestamps only (standing rule) — re-exported from the house
 * formatter so backup surfaces keep importing it from here.
 */
export { formatTimestamp };

/** Tenant column value — the platform tree renders as "Platform", never blank. */
export function tenantLabel(tenantName: string | null): string {
  return tenantName ?? "Platform";
}

/** Tree column value — plain labels (§10); the platform tree is just "Knowledge". */
export function treeLabel(tree: string): string {
  switch (tree) {
    case "knowledge":
    case "platform_knowledge":
      return "Knowledge";
    case "guidance":
      return "Guidance";
    case "studies":
      return "Studies";
    case "documents":
      return "Documents";
    default:
      return tree;
  }
}

export interface SyncStatus {
  label: "In sync" | "Stale" | "Out of sync" | "—";
  pill: "pillOk" | "pillWarn" | "pillBad" | "";
}

/**
 * §10 in-sync labeling: pillOk when in sync; pillWarn "Stale" when out of sync
 * beyond the §4.5 threshold (server-computed); pillBad otherwise. A tree with
 * no attempt yet renders "—" (nothing to judge).
 */
export function syncStatus(row: { inSync: boolean; stale: boolean; lastAttemptAt: string | null }): SyncStatus {
  if (row.inSync) return { label: "In sync", pill: "pillOk" };
  if (row.lastAttemptAt === null) return { label: "—", pill: "" };
  if (row.stale) return { label: "Stale", pill: "pillWarn" };
  return { label: "Out of sync", pill: "pillBad" };
}

/** Truncate for the Error column; the full text rides the title tooltip (standing default). */
export function truncateError(error: string | null, max = 80): { text: string; truncated: boolean } {
  if (!error) return { text: "—", truncated: false };
  if (error.length <= max) return { text: error, truncated: false };
  return { text: error.slice(0, max - 1) + "…", truncated: true };
}

export interface BackupTreeSummaryRow {
  tenantName: string | null;
  tree: string;
  firstPushAt: string | null;
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  inSync: boolean;
  stale: boolean;
  lastError: string | null;
}

export interface BackupSummary {
  /** Distinct tenants with any tree row (platform excluded). */
  tenantCount: number;
  hasPlatform: boolean;
  totalTrees: number;
  inSyncTrees: number;
  /** The weakest guarantee: the oldest last-success across in-sync trees; null when none. */
  oldestLastSuccessAt: string | null;
  /** The freshest evidence: the newest last-success across all trees; null when none. */
  latestLastSuccessAt: string | null;
  /** Only the trees needing attention (out of sync or stale, with at least one attempt). */
  problems: BackupTreeSummaryRow[];
  healthy: boolean;
}

/**
 * §10 (owner-revised 2026-08-03, option A): healthy = one summary line, nothing
 * enumerated; unhealthy = problems only. A tree with no attempt yet is not a
 * problem — it appears at its first write (matching the empty-state copy).
 */
export function summarizeTrees(rows: BackupTreeSummaryRow[]): BackupSummary {
  const tenants = new Set<string>();
  let hasPlatform = false;
  let inSyncTrees = 0;
  let oldest: string | null = null;
  let latest: string | null = null;
  const problems: BackupTreeSummaryRow[] = [];
  for (const row of rows) {
    if (row.tenantName === null) hasPlatform = true;
    else tenants.add(row.tenantName);
    if (row.lastSuccessAt && (latest === null || row.lastSuccessAt > latest)) latest = row.lastSuccessAt;
    if (row.inSync) {
      inSyncTrees += 1;
      if (row.lastSuccessAt && (oldest === null || row.lastSuccessAt < oldest)) oldest = row.lastSuccessAt;
    } else if (row.lastAttemptAt !== null) {
      problems.push(row);
    }
  }
  problems.sort((a, b) => tenantLabel(a.tenantName).localeCompare(tenantLabel(b.tenantName)) || a.tree.localeCompare(b.tree));
  return {
    tenantCount: tenants.size,
    hasPlatform,
    totalTrees: rows.length,
    inSyncTrees,
    oldestLastSuccessAt: oldest,
    latestLastSuccessAt: latest,
    problems,
    healthy: rows.length > 0 && problems.length === 0 && inSyncTrees === rows.length,
  };
}

/**
 * Owner doctrine (2026-08-03): tenants cannot individually "disconnect" —
 * the destination is platform-wide. When EVERY tree is behind it is a
 * destination-level incident (one banner, no tenant enumeration); when only
 * SOME trees are behind while others push fine, that is the rare
 * tampering/divergence alarm and exactly those trees are named.
 */
export type BackupIncident =
  | { kind: "empty" }
  | { kind: "healthy" }
  /** Rows exist that were never attempted yet — the initial sync is in flight. */
  | { kind: "syncing"; done: number; total: number }
  | { kind: "destination"; behind: number; lastPushAt: string | null; error: string | null }
  | { kind: "partial"; problems: BackupTreeSummaryRow[] };

export function classifyIncident(s: BackupSummary): BackupIncident {
  if (s.totalTrees === 0) return { kind: "empty" };
  if (s.healthy) return { kind: "healthy" };
  if (s.problems.length === 0) {
    // Nothing failing, not everything synced: first sync (or a fresh
    // re-setup) still in flight — never render this as an incident.
    return { kind: "syncing", done: s.inSyncTrees, total: s.totalTrees };
  }
  if (s.problems.length === s.totalTrees) {
    // Whole estate behind → one cause. Surface the most common scrubbed error.
    const counts = new Map<string, number>();
    for (const p of s.problems) {
      if (p.lastError) counts.set(p.lastError, (counts.get(p.lastError) ?? 0) + 1);
    }
    let error: string | null = null;
    let best = 0;
    for (const [msg, n] of counts) {
      if (n > best) {
        best = n;
        error = msg;
      }
    }
    return { kind: "destination", behind: s.problems.length, lastPushAt: s.latestLastSuccessAt, error };
  }
  return { kind: "partial", problems: s.problems };
}

/** "Failing since" for a problem row: the last success, or "never succeeded". */
export function failingSince(row: BackupTreeSummaryRow): string {
  return row.lastSuccessAt ? `since ${formatTimestamp(row.lastSuccessAt)}` : "never succeeded";
}
